<template>
  <div class="app-modal" :id="modalId">
    <transition
      mode="out-in"
      enter-active-class="animate__animated animate__fadeIn"
      leave-active-class="animate__animated animate__fadeOut"
    >
      <div
        class="app-modal-backdrop"
        :class="{ 'docPrint': !props.extendedModal }"
        ref="alertModal"
        v-if="props.showModal && !isEsc"
      >
        <dialog
          class="app-modal-content"
          :class="[{ '__large': props.isSizedUp }, { '__overflow': props.isOverflow }]"
          ref="modalContentRef"
          aria-labelledby="modalHeader"
          open
        >
          <header>
            <h1 id="modalHeader">
              <!-- title: should describe modal -->
              <slot name="heading"></slot>
            </h1>
          </header>
          <main>
            <!-- main content: any visible HTML elements -->
            <slot name="content"></slot>
          </main>
          <footer>
            <!-- buttons, links, etc. -->
            <slot name="footing"></slot>
          </footer>
        </dialog>
      </div>
    </transition>
  </div>
</template>


<script setup>
import { ref, watch, nextTick, onBeforeUnmount } from 'vue';

const props = defineProps({
  modalId: {
    type: String,
    required: true,
  },
  isSizedUp: {
    type: Boolean,
    required: false,
    default: false,
  },
  isOverflow: {
    type: Boolean,
    required: false,
    default: false,
  },
  showModal: {
    type: Boolean,
    required: true,
  },
  allowEsc: {
    type: Boolean,
    required: false,
    default: true,
  },
  extendedModal: {
    type: Boolean,
    default: false,
  },
});

const isEsc = ref(false);
const modalContentRef = ref(null);
const firstFocusableElement = ref(null);
const lastFocusableElement = ref(null);
const focusableElements = ref([]);

const init = () => {
  if (!modalContentRef.value) return;

  const focusableSelectors = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
  focusableElements.value = Array.from(modalContentRef.value.querySelectorAll(focusableSelectors));
  firstFocusableElement.value = focusableElements.value[0];
  lastFocusableElement.value = focusableElements.value[focusableElements.value.length - 1];

  if (firstFocusableElement.value && !props.isOverflow) {
    firstFocusableElement.value.focus();
  }
};

const bindKeys = (e) => {
  if (props.allowEsc) {
    const isEscPressed = e.key === 'Escape' || e.keyCode === 27;
    if (isEscPressed) {
      isEsc.value = true;
      return;
    }
  }

  const isTabPressed = e.key === 'Tab' || e.keyCode === 9;
  if (!isTabPressed || !modalContentRef.value) {
    return;
  }

  const activeElement = document.activeElement;

  if (e.shiftKey) { // Shift + Tab
    if (activeElement === firstFocusableElement.value) {
      lastFocusableElement.value.focus();
      e.preventDefault();
    }
  } else if (activeElement === lastFocusableElement.value) {
    // Tab
    firstFocusableElement.value.focus();
    e.preventDefault();
  }

};

watch(() => props.showModal, (newVal) => {
  if (newVal) {
    isEsc.value = false;
    nextTick(() => {
      init();
    });
    document.addEventListener('keydown', bindKeys);
  } else {
    document.removeEventListener('keydown', bindKeys);
  }
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', bindKeys);
});
</script>


<style lang="scss" scoped>
@import "~@ridmv/web-themes/styles/modal";
.app-modal {
  .docPrint{
    z-index: 101 !important;
  }
  .app-modal-backdrop {
    display: flex;
    justify-content: center;
    align-items: center;
    dialog {
      position: relative;   
      margin: auto;         
      inset: unset;         
      border: none;
      padding: 0;
      max-width: none;
      .app-modal-content {
        &.__overflow {      // add vertical scroll within modal if content gets too long
          height: 100%;
          overflow-y: auto;
          width: 100%;

          @media print {
            width: 220% !important;
            overflow-y: hidden;
            height: 115% !important;
          }
        }
      }
    }
  }

  dialog::backdrop {
    background: transparent;
  }

}

@media only screen and (max-width: 599px) {
  .app-modal {
    .app-modal-backdrop {
      display: flex;
      justify-content: center;
      align-items: center;
      dialog {
        position: relative;   
        margin: auto;         
        inset: unset;         
        border: none;
        padding: 0;
        max-width: none;
        .app-modal-content {
          &.__overflow {      // add vertical scroll within modal if content gets too long
            height: 100vh;
            overflow-y: auto;
            width: 100%;

            @media print {
              width: 220% !important;
              overflow-y: hidden;
              height: 115% !important;
            }
          }
        }
      }
    }
  }
}
</style>
